import React, { Suspense } from 'react';
import tinymce from 'tinymce';
import { Skeleton } from '@chakra-ui/react';
import { uploadImage } from '../../features/email/service';

const TinyMceEditorBase = React.lazy(() => import('./base'));

// eslint-disable-next-line import/no-default-export
export default function TinyMCEEditorWithImageUpload(
    props: React.ComponentProps<typeof TinyMceEditorBase> & {
        onFinishUpload?: () => void;
        onStartUpload?: () => void;
        containerHeight?: number;
    }
) {
    const { onFinishUpload, onStartUpload, containerHeight } = props;

    return (
        <Suspense fallback={<Skeleton height={containerHeight} />}>
            <TinyMceEditorBase
                init={{
                    base_url: 'assets/tinymce',
                    contextmenu: false,
                    elementpath: false,
                    menubar: false,
                    branding: false,
                    height: containerHeight,
                    width: '100%',
                    plugins: [
                        'advlist',
                        'anchor',
                        'autolink',
                        'image',
                        'link',
                        'lists',
                        'wordcount',
                        'code',
                    ],
                    toolbar:
                        'undo redo | bold italic forecolor | alignleft aligncenter alignright alignjustify | link image | code',
                    paste_data_images: true,
                    init_instance_callback() {
                        // terrible hack to show the character count instead of word count
                        document
                            .querySelectorAll('button.tox-statusbar__wordcount')
                            .forEach((button) => {
                                (button as HTMLButtonElement).click();
                                // eslint-disable-next-line no-param-reassign
                                (button as HTMLButtonElement).disabled = true;
                            });
                    },
                    images_upload_handler: async (blobInfo, progress) => {
                        // In case which the max file size is 2Mb
                        if (blobInfo.blob().size > 1024 * 1024 * 2) {
                            // eslint-disable-next-line prefer-promise-reject-errors
                            return Promise.reject({
                                message: 'File is too large. The maximum is 2MB.',
                                remove: true,
                            });
                        }

                        if (onStartUpload != null) onStartUpload();

                        let formData = new FormData();

                        formData.append('file', blobInfo.blob(), blobInfo.filename());

                        try {
                            let response = await uploadImage(formData, (progressEvent) => {
                                // show 99% for smoke actually upload the photo
                                progress(
                                    Math.round(Math.min((progressEvent?.progress ?? 0) * 100, 99))
                                );
                            });

                            if (response.public_url == null) {
                                throw Error('Unable to get the uploaded image');
                            }

                            if (onFinishUpload != null) onFinishUpload();

                            return response.public_url;
                        } catch (e) {
                            if (onFinishUpload != null) onFinishUpload();

                            // eslint-disable-next-line prefer-promise-reject-errors
                            return Promise.reject({ message: (e as Error).message, remove: true });
                        }
                    },
                    setup: (editor) => {
                        editor.on('drop', () => {
                            setTimeout(() => {
                                tinymce.activeEditor?.dom.setAttrib(
                                    tinymce.activeEditor.dom.select('img'),
                                    'height',
                                    'auto'
                                );
                                tinymce.activeEditor?.dom.setAttrib(
                                    tinymce.activeEditor.dom.select('img'),
                                    'width',
                                    '200px'
                                );
                            }, 500);
                        });
                    },
                }}
                {...props}
            />
        </Suspense>
    );
}

TinyMCEEditorWithImageUpload.defaultProps = {
    containerHeight: 300,
};
